import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from './Layout/DashboardLayout';
import api, { mealPlans as mealPlansApi, users as usersApi } from '../services/api';
import './MealPlanManagement.css';

const MEAL_TYPES = [
  { key: 'Pequeno-Almoço', icon: '🌅', time: '07:30' },
  { key: 'Lanche Manhã', icon: '🍎', time: '10:00' },
  { key: 'Almoço', icon: '🍽️', time: '12:30' },
  { key: 'Lanche Tarde', icon: '🥤', time: '16:00' },
  { key: 'Jantar', icon: '🌙', time: '19:30' },
  { key: 'Ceia', icon: '🥛', time: '22:00' },
];

const DAYS_OF_WEEK = [
  'Segunda-feira', 'Terça-feira', 'Quarta-feira',
  'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'
];

const GOALS = [
  'Emagrecimento', 'Ganho Muscular', 'Manutenção',
  'Saúde Geral', 'Performance Desportiva', 'Definição Muscular'
];

const emptyItem = () => ({
  foodName: '', quantity: '', unit: 'g', calories: 0,
  protein: 0, carbs: 0, fat: 0, orderIndex: 0, notes: ''
});

const emptyMeal = (mealType, idx) => ({
  mealType: mealType.key,
  name: '',
  time: mealType.time,
  calories: 0, protein: 0, carbs: 0, fat: 0,
  orderIndex: idx,
  notes: '',
  items: [emptyItem()]
});

const emptyDay = (dayName, idx) => ({
  dayName,
  orderIndex: idx,
  dayCalories: 0, dayProtein: 0, dayCarbs: 0, dayFat: 0,
  notes: '',
  meals: MEAL_TYPES.map((mt, i) => emptyMeal(mt, i))
});

const MealPlanManagement = ({ isAdmin = false }) => {
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userFilterSearch, setUserFilterSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list | view | create | edit
  const [expandedDay, setExpandedDay] = useState(null);
  const [expandedMeal, setExpandedMeal] = useState(null);

  // AI Generation state
  const [showAIForm, setShowAIForm] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMealForm, setAiMealForm] = useState({ dietaryPreferences: '', calories: 2000, goals: '' });
  const [generatedAIPlan, setGeneratedAIPlan] = useState(null);
  const [showAIPreview, setShowAIPreview] = useState(false);

  // Form state for create/edit
  const [form, setForm] = useState({
    title: '', description: '', goal: '', totalCalories: 2000,
    protein: 150, carbs: 250, fat: 65, startDate: '', endDate: '',
    notes: '', userId: '', days: []
  });

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (isAdmin && selectedUser) {
        response = await mealPlansApi.getByUser(selectedUser);
      } else {
        response = await mealPlansApi.getAll();
      }
      setPlans(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedUser]);

  const loadUsers = useCallback(async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar utilizadores:', error);
    }
  }, []);

  useEffect(() => {
    loadPlans();
    if (isAdmin) loadUsers();
  }, [loadPlans, loadUsers, isAdmin]);

  // ============================================
  // CRUD Operations
  // ============================================

  const handleCreate = () => {
    setForm({
      title: '', description: '', goal: 'Manutenção', totalCalories: 2000,
      protein: 150, carbs: 250, fat: 65, startDate: '', endDate: '',
      notes: '', userId: selectedUser || '',
      days: DAYS_OF_WEEK.map((d, i) => emptyDay(d, i))
    });
    setExpandedDay(0);
    setExpandedMeal(null);
    setViewMode('create');
  };

  const handleEdit = (plan) => {
    setForm({
      title: plan.title,
      description: plan.description || '',
      goal: plan.goal || '',
      totalCalories: plan.totalCalories,
      protein: plan.protein,
      carbs: plan.carbs,
      fat: plan.fat,
      startDate: plan.startDate ? plan.startDate.split('T')[0] : '',
      endDate: plan.endDate ? plan.endDate.split('T')[0] : '',
      notes: plan.notes || '',
      userId: plan.userId,
      days: plan.days && plan.days.length > 0
        ? plan.days.map(d => ({
            ...d,
            meals: d.meals && d.meals.length > 0
              ? d.meals.map(m => ({
                  ...m,
                  items: m.items && m.items.length > 0 ? m.items : [emptyItem()]
                }))
              : MEAL_TYPES.map((mt, i) => emptyMeal(mt, i))
          }))
        : DAYS_OF_WEEK.map((d, i) => emptyDay(d, i))
    });
    setSelectedPlan(plan);
    setExpandedDay(0);
    setExpandedMeal(null);
    setViewMode('edit');
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Por favor, insira um título para o plano');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        userId: form.userId ? parseInt(form.userId) : 0,
        startDate: form.startDate || null,
        endDate: form.endDate || null
      };

      if (viewMode === 'create') {
        await mealPlansApi.create(payload);
        toast.success('Plano alimentar criado com sucesso! 🎉');
      } else {
        await mealPlansApi.update(selectedPlan.id, payload);
        toast.success('Plano alimentar atualizado! ✅');
      }

      setViewMode('list');
      setSelectedPlan(null);
      loadPlans();
    } catch (error) {
      console.error('Erro ao guardar plano:', error);
      toast.error('Erro ao guardar plano alimentar');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este plano alimentar?')) return;
    try {
      await mealPlansApi.delete(id);
      toast.success('Plano eliminado!');
      if (selectedPlan?.id === id) {
        setSelectedPlan(null);
        setViewMode('list');
      }
      loadPlans();
    } catch (error) {
      toast.error('Erro ao eliminar plano');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await mealPlansApi.toggleActive(id);
      toast.success('Estado atualizado!');
      loadPlans();
    } catch (error) {
      toast.error('Erro ao alterar estado');
    }
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setExpandedDay(0);
    setExpandedMeal(null);
    setViewMode('view');
  };

  // ============================================
  // AI Meal Plan Generation
  // ============================================

  const generateAIMealPlan = async () => {
    if (!aiMealForm.goals.trim()) {
      toast.error('Por favor, descreva os seus objetivos nutricionais.');
      return;
    }

    setShowAIForm(false);
    setAiLoading(true);
    toast.info('🤖 A gerar plano alimentar com IA... aguarde por favor');

    try {
      const response = await api.post('/AIPersonalTrainer/generate-meal-plan', aiMealForm);
      setGeneratedAIPlan({
        content: response.data.mealPlan,
        calories: aiMealForm.calories,
        goals: aiMealForm.goals
      });
      setShowAIPreview(true);
      toast.success('Plano alimentar gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar plano alimentar:', error);
      toast.error('Erro ao gerar plano alimentar. Tente novamente.');
      setShowAIForm(true);
    } finally {
      setAiLoading(false);
    }
  };

  const approveAIMealPlan = async () => {
    if (!generatedAIPlan) return;

    setAiLoading(true);
    toast.info('A guardar plano alimentar...');

    try {
      await api.post('/AIPersonalTrainer/approve-meal-plan', {
        title: `Plano Alimentar IA - ${generatedAIPlan.goals || 'Personalizado'}`,
        description: `Plano gerado por IA com objetivo: ${generatedAIPlan.goals}`,
        goal: generatedAIPlan.goals || 'Manutenção',
        calories: generatedAIPlan.calories || 2000,
        content: generatedAIPlan.content
      });

      toast.success('✅ Plano alimentar guardado com sucesso! (+10 tokens)');
      setShowAIPreview(false);
      setGeneratedAIPlan(null);
      loadPlans();
    } catch (error) {
      console.error('Erro ao guardar plano:', error);
      toast.error('Erro ao guardar plano alimentar.');
    } finally {
      setAiLoading(false);
    }
  };

  // ============================================
  // Form helpers for days/meals/items
  // ============================================

  const updateDay = (dayIdx, field, value) => {
    const newDays = [...form.days];
    newDays[dayIdx] = { ...newDays[dayIdx], [field]: value };
    setForm({ ...form, days: newDays });
  };

  const updateMeal = (dayIdx, mealIdx, field, value) => {
    const newDays = [...form.days];
    const newMeals = [...newDays[dayIdx].meals];
    newMeals[mealIdx] = { ...newMeals[mealIdx], [field]: value };
    newDays[dayIdx] = { ...newDays[dayIdx], meals: newMeals };
    setForm({ ...form, days: newDays });
  };

  const updateItem = (dayIdx, mealIdx, itemIdx, field, value) => {
    const newDays = [...form.days];
    const newMeals = [...newDays[dayIdx].meals];
    const newItems = [...newMeals[mealIdx].items];
    newItems[itemIdx] = { ...newItems[itemIdx], [field]: value };
    newMeals[mealIdx] = { ...newMeals[mealIdx], items: newItems };
    newDays[dayIdx] = { ...newDays[dayIdx], meals: newMeals };
    setForm({ ...form, days: newDays });
  };

  const addItem = (dayIdx, mealIdx) => {
    const newDays = [...form.days];
    const newMeals = [...newDays[dayIdx].meals];
    const items = [...newMeals[mealIdx].items, { ...emptyItem(), orderIndex: newMeals[mealIdx].items.length }];
    newMeals[mealIdx] = { ...newMeals[mealIdx], items };
    newDays[dayIdx] = { ...newDays[dayIdx], meals: newMeals };
    setForm({ ...form, days: newDays });
  };

  const removeItem = (dayIdx, mealIdx, itemIdx) => {
    const newDays = [...form.days];
    const newMeals = [...newDays[dayIdx].meals];
    const items = newMeals[mealIdx].items.filter((_, i) => i !== itemIdx);
    newMeals[mealIdx] = { ...newMeals[mealIdx], items: items.length ? items : [emptyItem()] };
    newDays[dayIdx] = { ...newDays[dayIdx], meals: newMeals };
    setForm({ ...form, days: newDays });
  };

  const copyDayToAll = (sourceDayIdx) => {
    const sourceDay = form.days[sourceDayIdx];
    const newDays = form.days.map((day, idx) => {
      if (idx === sourceDayIdx) return day;
      return {
        ...day,
        dayCalories: sourceDay.dayCalories,
        dayProtein: sourceDay.dayProtein,
        dayCarbs: sourceDay.dayCarbs,
        dayFat: sourceDay.dayFat,
        meals: sourceDay.meals.map(m => ({
          ...m,
          items: m.items.map(i => ({ ...i }))
        }))
      };
    });
    setForm({ ...form, days: newDays });
    toast.success(`Refeições de ${sourceDay.dayName} copiadas para todos os dias!`);
  };

  // ============================================
  // Macro calculation helpers
  // ============================================

  const calcMealMacros = (items) => {
    return (items || []).reduce((acc, item) => ({
      calories: acc.calories + (item.calories || 0),
      protein: acc.protein + (item.protein || 0),
      carbs: acc.carbs + (item.carbs || 0),
      fat: acc.fat + (item.fat || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  const calcDayMacros = (meals) => {
    return (meals || []).reduce((acc, meal) => {
      const mealMacros = calcMealMacros(meal.items);
      return {
        calories: acc.calories + mealMacros.calories,
        protein: acc.protein + mealMacros.protein,
        carbs: acc.carbs + mealMacros.carbs,
        fat: acc.fat + mealMacros.fat,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  // ============================================
  // RENDER: Macro bar
  // ============================================
  const MacroBar = ({ label, value, max, color, unit = 'g' }) => {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
      <div className="mp-macro-bar">
        <div className="mp-macro-label">
          <span>{label}</span>
          <span className="mp-macro-value">{Math.round(value)}{unit}</span>
        </div>
        <div className="mp-macro-track">
          <div className="mp-macro-fill" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER: Plan List View
  // ============================================
  const renderListView = () => (
    <>
      <div className="mp-header">
        <div className="mp-header-left">
          <h2>🥗 {isAdmin ? 'Gestão de Planos Alimentares' : 'O Meu Plano Alimentar'}</h2>
          {isAdmin && (
            <span className="mp-count-badge">{plans.length} plano{plans.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="mp-header-actions">
          <button className="mp-btn mp-btn-ai" onClick={() => setShowAIForm(true)} disabled={aiLoading}>
            🤖 Gerar por IA
          </button>
          {isAdmin && (
            <button className="mp-btn mp-btn-primary" onClick={handleCreate}>
              <span>+</span> Novo Plano
            </button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="mp-filters">
          <div className="mp-filter-group" style={{ position: 'relative' }}>
            <label>👤 Filtrar por Utilizador:</label>
            <input
              type="text"
              placeholder="Pesquisar utilizador..."
              value={userFilterSearch}
              onChange={(e) => { setUserFilterSearch(e.target.value); if (!e.target.value) setSelectedUser(''); }}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '4px' }}
            />
            {userFilterSearch && !selectedUser && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: '200px', overflowY: 'auto' }}>
                <div onClick={() => { setSelectedUser(''); setUserFilterSearch(''); }} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '14px', color: '#64748b', borderBottom: '1px solid #f1f5f9' }} onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseOut={(e) => e.currentTarget.style.background = 'white'}>Todos os utilizadores</div>
                {users.filter(u => u.name?.toLowerCase().includes(userFilterSearch.toLowerCase()) || u.email?.toLowerCase().includes(userFilterSearch.toLowerCase())).map(u => (
                  <div key={u.id} onClick={() => { setSelectedUser(String(u.id)); setUserFilterSearch(u.name); }} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '14px' }} onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseOut={(e) => e.currentTarget.style.background = 'white'}>
                    <div style={{ fontWeight: '500', color: '#334155' }}>{u.name}</div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>{u.email}</div>
                  </div>
                ))}
              </div>
            )}
            {selectedUser && (
              <button onClick={() => { setSelectedUser(''); setUserFilterSearch(''); }} style={{ marginTop: '4px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontSize: '12px', cursor: 'pointer' }}>
                ✕ Limpar filtro
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="mp-loading">
          <div className="mp-spinner" />
          <p>A carregar planos alimentares...</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="mp-empty">
          <div className="mp-empty-icon">🥗</div>
          <h3>{isAdmin ? 'Sem planos alimentares' : 'Ainda não tem um plano alimentar'}</h3>
          <p>{isAdmin ? 'Crie um novo plano para começar.' : 'O seu treinador irá criar um plano personalizado para si.'}</p>
          {isAdmin && (
            <button className="mp-btn mp-btn-primary" onClick={handleCreate}>
              Criar Primeiro Plano
            </button>
          )}
        </div>
      ) : (
        <div className="mp-grid">
          {plans.map(plan => {
            const totalMeals = (plan.days || []).reduce((sum, d) => sum + (d.meals?.length || 0), 0);
            return (
              <div key={plan.id} className={`mp-card ${!plan.isActive ? 'mp-card-inactive' : ''}`}>
                <div className="mp-card-top">
                  <div className="mp-card-header">
                    <h3>{plan.title}</h3>
                    <div className="mp-card-badges">
                      {plan.goal && <span className="mp-badge mp-badge-goal">{plan.goal}</span>}
                      <span className={`mp-badge ${plan.isActive ? 'mp-badge-active' : 'mp-badge-inactive'}`}>
                        {plan.isActive ? '✅ Ativo' : '⏸️ Inativo'}
                      </span>
                    </div>
                  </div>
                  {plan.description && (
                    <p className="mp-card-desc">{plan.description}</p>
                  )}
                  {isAdmin && plan.userName && (
                    <div className="mp-card-user">👤 {plan.userName}</div>
                  )}
                </div>

                <div className="mp-card-macros">
                  <div className="mp-macro-item">
                    <span className="mp-macro-icon">🔥</span>
                    <span className="mp-macro-num">{plan.totalCalories}</span>
                    <span className="mp-macro-unit">kcal</span>
                  </div>
                  <div className="mp-macro-item">
                    <span className="mp-macro-icon">🥩</span>
                    <span className="mp-macro-num">{Math.round(plan.protein)}g</span>
                    <span className="mp-macro-unit">Proteína</span>
                  </div>
                  <div className="mp-macro-item">
                    <span className="mp-macro-icon">🍞</span>
                    <span className="mp-macro-num">{Math.round(plan.carbs)}g</span>
                    <span className="mp-macro-unit">Hidratos</span>
                  </div>
                  <div className="mp-macro-item">
                    <span className="mp-macro-icon">🥑</span>
                    <span className="mp-macro-num">{Math.round(plan.fat)}g</span>
                    <span className="mp-macro-unit">Gordura</span>
                  </div>
                </div>

                <div className="mp-card-stats">
                  <span>📅 {plan.days?.length || 0} dias</span>
                  <span>🍽️ {totalMeals} refeições</span>
                  {plan.startDate && (
                    <span>📆 {new Date(plan.startDate).toLocaleDateString('pt-PT')}</span>
                  )}
                </div>

                <div className="mp-card-actions">
                  <button className="mp-btn mp-btn-view" onClick={() => handleViewPlan(plan)}>
                    👁️ Ver Plano
                  </button>
                  {isAdmin && (
                    <>
                      <button className="mp-btn mp-btn-edit" onClick={() => handleEdit(plan)}>
                        ✏️ Editar
                      </button>
                      <button className="mp-btn mp-btn-toggle" onClick={() => handleToggleActive(plan.id)}>
                        {plan.isActive ? '⏸️' : '▶️'}
                      </button>
                      <button className="mp-btn mp-btn-delete" onClick={() => handleDelete(plan.id)}>
                        🗑️
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // ============================================
  // RENDER: Plan Detail View (read-only)
  // ============================================
  const renderViewMode = () => {
    if (!selectedPlan) return null;
    const plan = selectedPlan;

    return (
      <>
        <div className="mp-header">
          <button className="mp-btn mp-btn-back" onClick={() => { setViewMode('list'); setSelectedPlan(null); }}>
            ← Voltar
          </button>
          <h2>🥗 {plan.title}</h2>
          {isAdmin && (
            <button className="mp-btn mp-btn-edit" onClick={() => handleEdit(plan)}>
              ✏️ Editar
            </button>
          )}
        </div>

        {/* Plan Summary */}
        <div className="mp-summary">
          <div className="mp-summary-info">
            {plan.description && <p className="mp-summary-desc">{plan.description}</p>}
            <div className="mp-summary-meta">
              {plan.goal && <span className="mp-badge mp-badge-goal">{plan.goal}</span>}
              {plan.userName && <span className="mp-summary-user">👤 {plan.userName}</span>}
              {plan.startDate && (
                <span>📆 {new Date(plan.startDate).toLocaleDateString('pt-PT')}
                  {plan.endDate && ` — ${new Date(plan.endDate).toLocaleDateString('pt-PT')}`}
                </span>
              )}
            </div>
            {plan.notes && <p className="mp-summary-notes">📝 {plan.notes}</p>}
          </div>

          <div className="mp-summary-macros">
            <div className="mp-summary-macro-circle">
              <span className="mp-macro-big">{plan.totalCalories}</span>
              <span>kcal/dia</span>
            </div>
            <MacroBar label="Proteína" value={plan.protein} max={plan.protein + 50} color="#e74c3c" />
            <MacroBar label="Hidratos" value={plan.carbs} max={plan.carbs + 50} color="#3498db" />
            <MacroBar label="Gordura" value={plan.fat} max={plan.fat + 30} color="#f39c12" />
          </div>
        </div>

        {/* Days Tabs */}
        <div className="mp-days-tabs">
          {(plan.days || []).map((day, idx) => (
            <button
              key={idx}
              className={`mp-day-tab ${expandedDay === idx ? 'mp-day-tab-active' : ''}`}
              onClick={() => { setExpandedDay(idx); setExpandedMeal(null); }}
            >
              {day.dayName}
            </button>
          ))}
        </div>

        {/* Selected Day */}
        {expandedDay !== null && plan.days && plan.days[expandedDay] && (
          <div className="mp-day-detail">
            <div className="mp-day-header-detail">
              <h3>{plan.days[expandedDay].dayName}</h3>
              {(() => {
                const macros = calcDayMacros(plan.days[expandedDay].meals);
                return (
                  <div className="mp-day-macros-summary">
                    <span>🔥 {macros.calories} kcal</span>
                    <span>🥩 {Math.round(macros.protein)}g P</span>
                    <span>🍞 {Math.round(macros.carbs)}g C</span>
                    <span>🥑 {Math.round(macros.fat)}g G</span>
                  </div>
                );
              })()}
            </div>

            <div className="mp-meals-list">
              {(plan.days[expandedDay].meals || []).map((meal, mealIdx) => {
                const mealIcon = MEAL_TYPES.find(mt => mt.key === meal.mealType)?.icon || '🍽️';
                const mealMacros = calcMealMacros(meal.items);
                const isExpanded = expandedMeal === mealIdx;

                return (
                  <div key={mealIdx} className={`mp-meal-card ${isExpanded ? 'mp-meal-expanded' : ''}`}>
                    <div className="mp-meal-header" onClick={() => setExpandedMeal(isExpanded ? null : mealIdx)}>
                      <div className="mp-meal-title">
                        <span className="mp-meal-icon">{mealIcon}</span>
                        <div>
                          <h4>{meal.mealType}</h4>
                          {meal.time && <span className="mp-meal-time">⏰ {meal.time}</span>}
                        </div>
                      </div>
                      <div className="mp-meal-quick-macros">
                        <span>{mealMacros.calories} kcal</span>
                        <span className="mp-expand-icon">{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mp-meal-detail">
                        {meal.name && <p className="mp-meal-name">{meal.name}</p>}
                        <div className="mp-meal-macros-bar">
                          <span className="mp-tag mp-tag-protein">🥩 {Math.round(mealMacros.protein)}g</span>
                          <span className="mp-tag mp-tag-carbs">🍞 {Math.round(mealMacros.carbs)}g</span>
                          <span className="mp-tag mp-tag-fat">🥑 {Math.round(mealMacros.fat)}g</span>
                        </div>

                        {meal.items && meal.items.length > 0 && (
                          <table className="mp-items-table">
                            <thead>
                              <tr>
                                <th>Alimento</th>
                                <th>Quantidade</th>
                                <th>Kcal</th>
                                <th>P</th>
                                <th>C</th>
                                <th>G</th>
                              </tr>
                            </thead>
                            <tbody>
                              {meal.items.map((item, iIdx) => (
                                <tr key={iIdx}>
                                  <td className="mp-item-name">{item.foodName}</td>
                                  <td>{item.quantity}{item.unit}</td>
                                  <td>{item.calories}</td>
                                  <td>{Math.round(item.protein)}g</td>
                                  <td>{Math.round(item.carbs)}g</td>
                                  <td>{Math.round(item.fat)}g</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {meal.notes && <p className="mp-meal-notes">📝 {meal.notes}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  // ============================================
  // RENDER: Create/Edit Form
  // ============================================
  const renderFormView = () => (
    <>
      <div className="mp-header">
        <button className="mp-btn mp-btn-back" onClick={() => { setViewMode('list'); setSelectedPlan(null); }}>
          ← Voltar
        </button>
        <h2>🥗 {viewMode === 'create' ? 'Novo Plano Alimentar' : 'Editar Plano Alimentar'}</h2>
        <button className="mp-btn mp-btn-primary" onClick={handleSave} disabled={loading}>
          {loading ? '⏳ A guardar...' : '💾 Guardar Plano'}
        </button>
      </div>

      {/* Basic Info */}
      <div className="mp-form-section">
        <h3>📋 Informações Gerais</h3>
        <div className="mp-form-grid">
          <div className="mp-form-group mp-form-wide">
            <label>Título *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: Plano para Emagrecimento - Semana 1"
            />
          </div>

          <div className="mp-form-group mp-form-wide">
            <label>Descrição</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Descrição do plano alimentar..."
              rows="3"
            />
          </div>

          <div className="mp-form-group">
            <label>🎯 Objetivo</label>
            <select value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })}>
              <option value="">Selecionar...</option>
              {GOALS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          {isAdmin && (
            <div className="mp-form-group">
              <label>👤 Atribuir a Utilizador</label>
              <select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}>
                <option value="">Selecionar...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </select>
            </div>
          )}

          <div className="mp-form-group">
            <label>📆 Data Início</label>
            <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </div>

          <div className="mp-form-group">
            <label>📆 Data Fim</label>
            <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Macro Targets */}
      <div className="mp-form-section">
        <h3>📊 Macros Alvo (por dia)</h3>
        <div className="mp-form-grid mp-form-macros">
          <div className="mp-form-group">
            <label>🔥 Calorias (kcal)</label>
            <input type="number" value={form.totalCalories} onChange={(e) => setForm({ ...form, totalCalories: parseInt(e.target.value) || 0 })} />
          </div>
          <div className="mp-form-group">
            <label>🥩 Proteína (g)</label>
            <input type="number" value={form.protein} onChange={(e) => setForm({ ...form, protein: parseFloat(e.target.value) || 0 })} />
          </div>
          <div className="mp-form-group">
            <label>🍞 Hidratos (g)</label>
            <input type="number" value={form.carbs} onChange={(e) => setForm({ ...form, carbs: parseFloat(e.target.value) || 0 })} />
          </div>
          <div className="mp-form-group">
            <label>🥑 Gordura (g)</label>
            <input type="number" value={form.fat} onChange={(e) => setForm({ ...form, fat: parseFloat(e.target.value) || 0 })} />
          </div>
        </div>
      </div>

      {/* Days Editor */}
      <div className="mp-form-section">
        <h3>📅 Plano Semanal</h3>

        <div className="mp-days-tabs">
          {form.days.map((day, dayIdx) => {
            const dayMacros = calcDayMacros(day.meals);
            return (
              <button
                key={dayIdx}
                className={`mp-day-tab ${expandedDay === dayIdx ? 'mp-day-tab-active' : ''}`}
                onClick={() => { setExpandedDay(dayIdx); setExpandedMeal(null); }}
              >
                <span>{day.dayName.substring(0, 3)}</span>
                {dayMacros.calories > 0 && (
                  <span className="mp-day-tab-kcal">{dayMacros.calories} kcal</span>
                )}
              </button>
            );
          })}
        </div>

        {expandedDay !== null && form.days[expandedDay] && (
          <div className="mp-day-editor">
            <div className="mp-day-editor-header">
              <h4>{form.days[expandedDay].dayName}</h4>
              <button className="mp-btn mp-btn-small mp-btn-copy" onClick={() => copyDayToAll(expandedDay)}>
                📋 Copiar para Todos os Dias
              </button>
            </div>

            {/* Day summary */}
            {(() => {
              const dayMacros = calcDayMacros(form.days[expandedDay].meals);
              return (
                <div className="mp-day-macros-summary mp-day-macros-editor">
                  <span className={dayMacros.calories > form.totalCalories ? 'mp-over' : ''}>
                    🔥 {dayMacros.calories} / {form.totalCalories} kcal
                  </span>
                  <span>🥩 {Math.round(dayMacros.protein)}g P</span>
                  <span>🍞 {Math.round(dayMacros.carbs)}g C</span>
                  <span>🥑 {Math.round(dayMacros.fat)}g G</span>
                </div>
              );
            })()}

            {/* Meals */}
            {form.days[expandedDay].meals.map((meal, mealIdx) => {
              const mealType = MEAL_TYPES.find(mt => mt.key === meal.mealType);
              const mealIcon = mealType?.icon || '🍽️';
              const isExpanded = expandedMeal === mealIdx;
              const mealMacros = calcMealMacros(meal.items);

              return (
                <div key={mealIdx} className={`mp-meal-editor ${isExpanded ? 'mp-meal-editor-expanded' : ''}`}>
                  <div className="mp-meal-editor-header" onClick={() => setExpandedMeal(isExpanded ? null : mealIdx)}>
                    <div className="mp-meal-editor-title">
                      <span className="mp-meal-icon">{mealIcon}</span>
                      <span>{meal.mealType}</span>
                      <span className="mp-meal-time-badge">{meal.time || '—'}</span>
                    </div>
                    <div className="mp-meal-editor-summary">
                      <span>{mealMacros.calories} kcal</span>
                      <span>{meal.items?.length || 0} alimentos</span>
                      <span className="mp-expand-icon">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mp-meal-editor-body">
                      <div className="mp-meal-inline-fields">
                        <div className="mp-form-group-inline">
                          <label>Nome da refeição</label>
                          <input
                            type="text"
                            value={meal.name || ''}
                            onChange={(e) => updateMeal(expandedDay, mealIdx, 'name', e.target.value)}
                            placeholder="Ex: Pequeno-almoço proteico"
                          />
                        </div>
                        <div className="mp-form-group-inline">
                          <label>Hora</label>
                          <input
                            type="time"
                            value={meal.time || ''}
                            onChange={(e) => updateMeal(expandedDay, mealIdx, 'time', e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Items table */}
                      <div className="mp-items-editor">
                        <table className="mp-items-table mp-items-edit-table">
                          <thead>
                            <tr>
                              <th>Alimento</th>
                              <th>Qtd</th>
                              <th>Un</th>
                              <th>Kcal</th>
                              <th>P(g)</th>
                              <th>C(g)</th>
                              <th>G(g)</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(meal.items || []).map((item, itemIdx) => (
                              <tr key={itemIdx}>
                                <td>
                                  <input
                                    type="text"
                                    value={item.foodName}
                                    onChange={(e) => updateItem(expandedDay, mealIdx, itemIdx, 'foodName', e.target.value)}
                                    placeholder="Ex: Peito de frango"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={item.quantity}
                                    onChange={(e) => updateItem(expandedDay, mealIdx, itemIdx, 'quantity', e.target.value)}
                                    placeholder="200"
                                    className="mp-input-small"
                                  />
                                </td>
                                <td>
                                  <select
                                    value={item.unit}
                                    onChange={(e) => updateItem(expandedDay, mealIdx, itemIdx, 'unit', e.target.value)}
                                    className="mp-input-small"
                                  >
                                    <option value="g">g</option>
                                    <option value="ml">ml</option>
                                    <option value="un">un</option>
                                    <option value="colher">colher</option>
                                    <option value="chávena">chávena</option>
                                    <option value="fatia">fatia</option>
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={item.calories}
                                    onChange={(e) => updateItem(expandedDay, mealIdx, itemIdx, 'calories', parseInt(e.target.value) || 0)}
                                    className="mp-input-small"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={item.protein}
                                    onChange={(e) => updateItem(expandedDay, mealIdx, itemIdx, 'protein', parseFloat(e.target.value) || 0)}
                                    className="mp-input-small"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={item.carbs}
                                    onChange={(e) => updateItem(expandedDay, mealIdx, itemIdx, 'carbs', parseFloat(e.target.value) || 0)}
                                    className="mp-input-small"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={item.fat}
                                    onChange={(e) => updateItem(expandedDay, mealIdx, itemIdx, 'fat', parseFloat(e.target.value) || 0)}
                                    className="mp-input-small"
                                  />
                                </td>
                                <td>
                                  <button className="mp-btn-icon mp-btn-remove" onClick={() => removeItem(expandedDay, mealIdx, itemIdx)} title="Remover">
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="mp-items-total">
                              <td colSpan="3"><strong>Total</strong></td>
                              <td><strong>{mealMacros.calories}</strong></td>
                              <td><strong>{Math.round(mealMacros.protein)}</strong></td>
                              <td><strong>{Math.round(mealMacros.carbs)}</strong></td>
                              <td><strong>{Math.round(mealMacros.fat)}</strong></td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                        <button className="mp-btn mp-btn-small mp-btn-add-item" onClick={() => addItem(expandedDay, mealIdx)}>
                          + Adicionar Alimento
                        </button>
                      </div>

                      <div className="mp-form-group-inline mp-meal-notes-field">
                        <label>Notas da refeição</label>
                        <input
                          type="text"
                          value={meal.notes || ''}
                          onChange={(e) => updateMeal(expandedDay, mealIdx, 'notes', e.target.value)}
                          placeholder="Ex: Beber 500ml de água"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="mp-form-section">
        <h3>📝 Notas Gerais</h3>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Notas adicionais sobre o plano alimentar..."
          rows="3"
          className="mp-form-textarea"
        />
      </div>

      <div className="mp-form-footer">
        <button className="mp-btn mp-btn-secondary" onClick={() => { setViewMode('list'); setSelectedPlan(null); }}>
          Cancelar
        </button>
        <button className="mp-btn mp-btn-primary mp-btn-large" onClick={handleSave} disabled={loading}>
          {loading ? '⏳ A guardar...' : '💾 Guardar Plano Alimentar'}
        </button>
      </div>
    </>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <DashboardLayout userType={isAdmin ? 'admin' : 'client'}>
      <div className="mp-container">
        {viewMode === 'list' && renderListView()}
        {viewMode === 'view' && renderViewMode()}
        {(viewMode === 'create' || viewMode === 'edit') && renderFormView()}

        {/* AI Meal Plan Form Modal */}
        {showAIForm && (
          <div className="modal-overlay" onClick={() => setShowAIForm(false)}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>🤖 Gerar Plano Alimentar por IA</h3>
                <button onClick={() => setShowAIForm(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-field">
                  <label>🔥 Calorias/Dia</label>
                  <input type="number" value={aiMealForm.calories} onChange={(e) => setAiMealForm({...aiMealForm, calories: parseInt(e.target.value) || 2000})} />
                </div>
                <div className="form-field">
                  <label>🎯 Objetivos</label>
                  <textarea value={aiMealForm.goals} onChange={(e) => setAiMealForm({...aiMealForm, goals: e.target.value})} placeholder="Ex: perder peso, ganhar massa muscular, manter peso..." rows="3"></textarea>
                </div>
                <div className="form-field">
                  <label>🥗 Preferências Alimentares</label>
                  <input type="text" value={aiMealForm.dietaryPreferences} onChange={(e) => setAiMealForm({...aiMealForm, dietaryPreferences: e.target.value})} placeholder="Ex: vegetariano, sem lactose, sem glúten..." />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setShowAIForm(false)}>Cancelar</button>
                <button className="btn-generate" onClick={generateAIMealPlan} disabled={aiLoading}>
                  {aiLoading ? '⏳ A gerar...' : '🤖 Gerar Plano'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Generated Plan Preview Modal */}
        {showAIPreview && generatedAIPlan && (
          <div className="modal-overlay">
            <div className="modal-box modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>✅ Plano Alimentar Gerado por IA</h3>
                <button onClick={() => setShowAIPreview(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="plan-preview">
                  <pre>{generatedAIPlan.content}</pre>
                </div>
                <div className="plan-info">
                  <p><strong>🎯 Objetivo:</strong> {generatedAIPlan.goals}</p>
                  <p><strong>🔥 Calorias:</strong> {generatedAIPlan.calories} kcal/dia</p>
                  <p>Será guardado nos seus planos alimentares com a nota <strong>"Gerado por IA"</strong></p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => { setShowAIPreview(false); setGeneratedAIPlan(null); }}>Rejeitar</button>
                <button className="btn-approve" onClick={approveAIMealPlan} disabled={aiLoading}>
                  {aiLoading ? '⏳ A guardar...' : '✅ Aprovar e Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Loading Overlay */}
        {aiLoading && !showAIForm && !showAIPreview && (
          <div className="modal-overlay">
            <div className="mp-ai-loading-box">
              <div className="mp-spinner" />
              <p>🤖 A IA está a gerar o seu plano alimentar...</p>
              <p className="mp-ai-loading-sub">Isto pode demorar alguns segundos</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MealPlanManagement;
